import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Popover,
  TextField,
  Typography,
  Box,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import { useState, useEffect } from "react";
import OutlinedSelectButton from "./OutlinedSelectButton";
import { WhereFilterOperators, whereFilterType } from "src/types/types";
import { FormLabel } from "react-bootstrap";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs, isDayjs } from "dayjs";
import ClearSelectButton from "./ClearSelectButton";

type AdvancedFilterSelectProps = {
  label: string;
  columnName: string;
  whereFilters: whereFilterType[];
  setWhereFilters: React.Dispatch<React.SetStateAction<whereFilterType[]>>;
  valueType: "string" | "int" | "boolean" | "date";
};

const AdvancedFilterSelect = ({
  label,
  columnName,
  whereFilters,
  setWhereFilters,
  valueType,
}: AdvancedFilterSelectProps) => {
  const getFilterDetails = () => {
    const filter = whereFilters.find((f) => f.filter_on.name === columnName);
    return {
      value: filter ? filter.filter_value : "",
      comparisonOperator: filter ? filter.comparison_operator : "eq",
    };
  };

  const { value: initialValue, comparisonOperator: initialOperator } =
    getFilterDetails();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tempValue, setTempValue] = useState<string>(initialValue);
  const [value, setValue] = useState<string>(initialValue);
  const [tempOperator, setTempOperator] =
    useState<WhereFilterOperators>(initialOperator);

  useEffect(() => {
    setTempValue(initialValue);
    setTempOperator(initialOperator);
  }, [initialValue, initialOperator]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApplyFilter = () => {
    setValue(tempValue);
    setWhereFilters((prevWhereFilters) => {
      const updatedFilters = [...prevWhereFilters];
      const filterIndex = updatedFilters.findIndex(
        (filter) => filter.filter_on.name === columnName,
      );

      let value_datatype = valueType;

      if (tempValue === "") {
        // Remove filter if value is empty
        if (filterIndex !== -1) {
          updatedFilters.splice(filterIndex, 1);
        }
      } else {
        if (filterIndex !== -1) {
          // Update existing filter
          updatedFilters[filterIndex] = {
            ...updatedFilters[filterIndex],
            filter_value: tempValue,
            comparison_operator: tempOperator,
            value_datatype,
          };
        } else {
          // Add new filter
          updatedFilters.push({
            filter_on: {
              group: "dataset",
              name: columnName,
              column: columnName,
            },
            comparison_operator: tempOperator,
            value_datatype,
            filter_value: tempValue,
          });
        }
      }

      return updatedFilters;
    });
    handleClose();
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    if (newValue === null) {
      setTempValue("");
    } else {
      setTempValue(newValue.toISOString());
    }
  };

  const handleOperatorChange = (event: SelectChangeEvent<string>) => {
    setTempOperator(event.target.value as WhereFilterOperators);
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTempValue(event.target.value);
  };

  const handleClearFilter = () => {
    setTempValue("");
    setValue("");
    setWhereFilters((prevWhereFilters) =>
      prevWhereFilters.filter((filter) => filter.filter_on.name !== columnName),
    );
    handleClose();
  };

  const operatorOptions = [
    { value: "eq", label: "=" },
    { value: "gt", label: ">" },
    { value: "lt", label: "<" },
    { value: "ge", label: ">=" },
    { value: "le", label: "<=" },
  ];

  const open = Boolean(anchorEl);
  const id = open ? "filter-popover" : undefined;

  const renderMenuitems = () => {
    // if type is boolean, only show eq operator
    if (valueType === "boolean") {
      return operatorOptions
        .filter((option) => option.value === "eq")
        .map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ));
    }

    return operatorOptions.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ));
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={0.5}>
        <OutlinedSelectButton endIcon={<ArrowDropDown />} onClick={handleClick}>
          <Typography>{value || label}</Typography>
        </OutlinedSelectButton>
        {tempValue && !open && (
          <ClearSelectButton onClick={handleClearFilter} />
        )}
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box p={2} minWidth={300}>
          <Typography variant="h6">{label}</Typography>
          <FormControl fullWidth size="small" sx={{ marginTop: 2 }}>
            <InputLabel>Operator</InputLabel>
            <Select
              value={tempOperator}
              onChange={handleOperatorChange}
              label={"Operator"}
            >
              {renderMenuitems()}
            </Select>
          </FormControl>
          {valueType === "boolean" && (
            <FormControl fullWidth size="small" sx={{ marginTop: 2 }}>
              <FormLabel>Value</FormLabel>
              <RadioGroup
                row
                aria-label={label}
                name={label}
                value={tempValue}
                onChange={handleValueChange}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="True"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="False"
                />
              </RadioGroup>
            </FormControl>
          )}
          {valueType === "string" && (
            <TextField
              fullWidth
              size="small"
              label="Value"
              placeholder="Enter value"
              value={tempValue}
              onChange={handleValueChange}
              sx={{ marginTop: 2 }}
            />
          )}
          {valueType === "int" && (
            <TextField
              type="number"
              fullWidth
              size="small"
              label="Value"
              placeholder="Enter value"
              value={tempValue}
              onChange={handleValueChange}
              sx={{ marginTop: 2 }}
            />
          )}
          {valueType === "date" && (
            <DatePicker
              value={isDayjs(tempValue) ? tempValue : null}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  helperText: "Select date",
                },
              }}
              sx={{ mt: 2 }}
            />
          )}
          <Box display="flex" justifyContent="flex-end" sx={{ marginTop: 2 }}>
            <Button
              onClick={handleApplyFilter}
              variant="contained"
              color="primary"
              disableElevation
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default AdvancedFilterSelect;
